import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Container,
  CssBaseline,
  Box,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControl,
  OutlinedInput,
  InputLabel,
  Paper,
  Divider,
} from "@mui/material";
import { useStores } from "../../use-stores";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useNotification } from "../../utils/NotificationsContext";
import Otp from "../components/login/Otp";
import CustomTypography from "../components/common/form/CustomTypography";
import { observer } from "mobx-react-lite";

const Login = () => {
  const { userStore } = useStores();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOtpPopup, setshowOtpPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { showError } = useNotification();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  async function handleLogin() {
    try {
      setLoading(true);
      userStore.reset();
      await userStore.login(email, password);
      let user = userStore.user;
      if (user) {
        if (user.token) {
          setLoading(false);
          navigate("/");
        } else {
          setLoading(false);
          await userStore.sendLoginOtp(email, password);
          setshowOtpPopup(true);
        }
      } else {
        setLoading(false);
        showError(
          "Error",
          "An error occurred during login, please check your email or password."
        );
      }
    } catch (error) {
      setLoading(false);
      showError("Error", "An error occurred during login.");
    }
  }

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      const authUrl = await userStore.userAPI.getGoogleAuthUrl();
      setLoading(false);

      if (authUrl && typeof authUrl === "string") {
        window.location.href = authUrl;
      } else {
        showError("Error", "Google auth URL is missing from backend response.");
      }
    } catch (_error) {
      setLoading(false);
      const error = _error as any;
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to start Google login.";
      showError("Error", errorMessage);
    }
  };

  const handleClosePoupUp = () => {
    setshowOtpPopup(false);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2.5 },
          background:
            "radial-gradient(circle at top left, rgba(25,118,210,0.15), transparent 45%), radial-gradient(circle at bottom right, rgba(46,125,50,0.12), transparent 40%), linear-gradient(145deg, #f8fbff 0%, #f5f7fb 100%)",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: "100%",
            maxWidth: 920,
            borderRadius: 3,
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" },
            border: "1px solid",
            borderColor: "rgba(25,118,210,0.12)",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.11)",
          }}
        >
          <Box
            sx={{
              p: { xs: 2.5, sm: 3.2, md: 3.8 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",

              backgroundColor: "#ffffff",
              borderRight: { md: "1px solid" },
              borderColor: { md: "rgba(25,118,210,0.12)" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 360,
                transform: { xs: "none", md: "translateY(-28px)" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <img
                  src="https://content.swageazy.com/logos/swageazy_logo_small.png"
                  alt="Swageazy"
                  style={{ height: "84px", width: "auto", objectFit: "contain" }}
                />
              </Box>

              <CustomTypography
                variant="body2"
                style={{ fontSize: "13px", color: "rgba(15,23,42,0.72)", }}
              >
                Sign in to access your admin dashboard, teams, reports, and workflows.
              </CustomTypography>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2.5, sm: 3.2, md: 3.8 } }}>
            <CustomTypography variant="heading4" style={{ fontWeight: 800, marginBottom: "0.4rem" }}>
              Login
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use your work account credentials to continue.
            </CustomTypography>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleLogin}
              disabled={loading}
              sx={{
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: "0.9rem",
                mb: 1.5,
                textTransform: "none",
              }}
            >
              Continue with Google
            </Button>

            <Divider sx={{ my: 1.5, fontWeight: 600, color: "text.secondary" }}>OR</Divider>

            <Box>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoFocus
                size="medium"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    minHeight: 44,
                  },
                  "& .MuiOutlinedInput-input": {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                  "& .MuiInputLabel-root": {
                    transform: "translate(14px, 11px) scale(1)",
                  },
                  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />

              <FormControl
                sx={{
                  margin: "auto",
                  width: "100%",
                  marginTop: "0.8rem",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    minHeight: 44,
                  },
                  "& .MuiOutlinedInput-input": {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                  "& .MuiInputLabel-root": {
                    transform: "translate(14px, 11px) scale(1)",
                  },
                  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
                variant="outlined"
              >
                <InputLabel htmlFor="password">Password *</InputLabel>
                <OutlinedInput
                  type={showPassword ? "text" : "password"}
                  margin="dense"
                  required
                  fullWidth
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword
                            ? "hide the password"
                            : "display the password"
                        }
                        onClick={handleClickShowPassword}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password*"
                />
              </FormControl>
              <InputLabel
                sx={{
                  position: "relative !important",
                  cursor: "pointer",
                  marginBottom: "8px",
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "end",
                  fontSize: "0.8rem",
                }}
                htmlFor="password"
              >
                <Link to={`/forget-password`}>Forget Password?</Link>
              </InputLabel>

              <Button
                fullWidth
                variant="contained"
                sx={{
                  mt: 0.6,
                  mb: 0.4,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: "0.92rem",
                  textTransform: "none",
                  boxShadow: "0 8px 14px rgba(25,118,210,0.24)",
                }}
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
      {showOtpPopup && (
        <Otp
          email={email}
          password={password}
          open={showOtpPopup}
          onCancel={handleClosePoupUp}
        />
      )}
    </Container>
  );
};

export default observer(Login);
